import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const DeletedAccountsAdmin = () => {
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchParams, setSearchParams] = useState({
    email: '',
    date_from: '',
    date_to: '',
    min_balance: '',
    max_balance: ''
  });

  // Load deleted accounts
  const loadDeletedAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminAPI.getDeletedAccounts(searchParams);
      setDeletedAccounts(response.accounts || []);
    } catch (error) {
      setError('Failed to load deleted accounts');
      console.error('Error loading deleted accounts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  // Load account details
  const loadAccountDetails = async (userId) => {
    setIsLoading(true);
    try {
      const [accountDetails, transactionHistory] = await Promise.all([
        adminAPI.getDeletedAccountDetails(userId),
        adminAPI.searchDeletedTransactions({ user_id: userId })
      ]);
      
      setSelectedAccount(accountDetails);
      setTransactions(transactionHistory.transactions || []);
    } catch (error) {
      setError('Failed to load account details');
      console.error('Error loading account details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Process refund
  const processRefund = async (userId, amount, reason) => {
    if (!window.confirm(`Process refund of $${amount} for deleted account?`)) return;
    
    setIsLoading(true);
    try {
      const response = await adminAPI.processDeletedAccountRefund(userId, amount, reason);
      if (response.success) {
        setSuccess('Refund processed successfully');
        loadAccountDetails(userId); // Refresh account details
      } else {
        throw new Error(response.message || 'Failed to process refund');
      }
    } catch (error) {
      setError(error.message || 'Failed to process refund');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDeletedAccounts();
  }, [loadDeletedAccounts]);

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>🗑️ Deleted Accounts Management</h2>
      
      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{ background: '#d4edda', color: '#155724', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
          {success}
        </div>
      )}

      {/* Search Filters */}
      <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>Search Filters</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Email:</label>
            <input
              type="email"
              value={searchParams.email}
              onChange={(e) => setSearchParams({...searchParams, email: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Date From:</label>
            <input
              type="date"
              value={searchParams.date_from}
              onChange={(e) => setSearchParams({...searchParams, date_from: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Date To:</label>
            <input
              type="date"
              value={searchParams.date_to}
              onChange={(e) => setSearchParams({...searchParams, date_to: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Min Balance:</label>
            <input
              type="number"
              step="0.01"
              value={searchParams.min_balance}
              onChange={(e) => setSearchParams({...searchParams, min_balance: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              placeholder="0.00"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Max Balance:</label>
            <input
              type="number"
              step="0.01"
              value={searchParams.max_balance}
              onChange={(e) => setSearchParams({...searchParams, max_balance: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              placeholder="1000.00"
            />
          </div>
        </div>
        <button 
          onClick={loadDeletedAccounts}
          disabled={isLoading}
          style={{ 
            background: '#007bff', 
            color: '#fff', 
            border: 'none', 
            padding: '8px 16px', 
            borderRadius: '4px', 
            marginTop: '12px',
            cursor: 'pointer'
          }}
        >
          🔍 Search
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Deleted Accounts List */}
        <div>
          <h3 style={{ marginBottom: '16px' }}>Deleted Accounts ({deletedAccounts.length})</h3>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {deletedAccounts.map((account) => (
              <div 
                key={account.original_user_id}
                onClick={() => loadAccountDetails(account.original_user_id)}
                style={{
                  background: selectedAccount?.original_user_id === account.original_user_id ? '#e3f2fd' : '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                      {account.original_email}
                    </h4>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
                      Deleted: {new Date(account.deletion_date).toLocaleDateString()}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#666' }}>
                      Reason: {account.reason || 'user_requested'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: account.final_balance > 0 ? '#28a745' : account.final_balance < 0 ? '#dc3545' : '#6c757d'
                    }}>
                      ${account.final_balance?.toFixed(2) || '0.00'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Final Balance
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Account Details */}
        <div>
          {selectedAccount ? (
            <div>
              <h3 style={{ marginBottom: '16px' }}>Account Details</h3>
              <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Email:</strong> {selectedAccount.original_email}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Deletion Date:</strong> {new Date(selectedAccount.deletion_date).toLocaleString()}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Final Balance:</strong> 
                  <span style={{ 
                    color: selectedAccount.final_balance > 0 ? '#28a745' : selectedAccount.final_balance < 0 ? '#dc3545' : '#6c757d',
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}>
                    ${selectedAccount.final_balance?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Transaction Count:</strong> {selectedAccount.transaction_count || 0}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Reason:</strong> {selectedAccount.reason || 'user_requested'}
                </div>

                {selectedAccount.final_balance > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px' }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#856404' }}>⚠️ Outstanding Balance</h5>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#856404' }}>
                      This account has a positive balance of ${selectedAccount.final_balance.toFixed(2)}.
                    </p>
                    <button
                      onClick={() => {
                        const amount = prompt('Enter refund amount:', selectedAccount.final_balance.toFixed(2));
                        const reason = prompt('Enter refund reason:', 'Account deletion refund');
                        if (amount && reason) {
                          processRefund(selectedAccount.original_user_id, parseFloat(amount), reason);
                        }
                      }}
                      style={{
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      💰 Process Refund
                    </button>
                  </div>
                )}
              </div>

              {/* Transaction History */}
              <h4 style={{ marginBottom: '12px' }}>Transaction History</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {transactions.map((transaction, index) => (
                  <div key={index} style={{
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          Transaction #{transaction.id || index + 1}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {transaction.type || 'Unknown Type'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {new Date(transaction.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold',
                          color: transaction.amount > 0 ? '#28a745' : '#dc3545'
                        }}>
                          {transaction.amount > 0 ? '+' : ''}${transaction.amount?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Balance: ${transaction.balance?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                    {transaction.description && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {transaction.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ 
              background: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '8px', 
              padding: '40px', 
              textAlign: 'center',
              color: '#6c757d'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👆</div>
              <p>Select an account to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeletedAccountsAdmin;
